import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import {
  Address,
  CampaignTariffSelectionData,
  IAktionen,
  IBusinessData,
  IConfirmationStatus,
  IContractDetails,
  IdCard,
  IERechnungsInfo,
  INewClientTrackingData,
  IOlavEntry,
  IOrderData,
  IOrderDetailsInfo,
  IOrganisation,
  IPersonalData,
  IProduktAuswahl,
  IProduktDetails,
  IProduktSelektion,
  IProduktVerfuegbarkeit,
  IUploadResult,
  IVertragskonto,
  NewClientContractDetailsViewModel,
  OfferStatusDTO,
  OrderIPDFLinks,
  PreisIndikation,
  Product,
} from '@mwe/models';
import { BehaviorSubject } from 'rxjs';
import { ProductCategoryEnum, ServiceStateEnum } from '@mwe/constants';
import { INewVoucherFormConfig } from '../../voucher/new-voucher/new-voucher.model';
import { withStorageServiceFeature } from '../../store-features/storage-service.feature';
import { withAuthenticationStateFeature } from '../../store-features/authentication.feature';
import { ConfirmationStatusState, withConfirmationStatusState } from '../../store-features/confirmation-status-state.feature';
import { MemoryCacheItem } from '../../cache/memory-cache.service';
import { getProductFromDetailsInfo, isProductCategoryFernwaerme, isProductCategoryStromOrGas, sortProductsByCategory } from '@mwe/utils';
import { computed, inject } from '@angular/core';
import { TariffSelectionStateService } from '../../tariff/selection/tariff-selection-state.service';

const STORAGE_KEY = 'new-client';

export type NewClientStoreType = InstanceType<typeof NewClientStore>;

const initialState = {
  tempProductSelectionForVoucher: undefined as IProduktSelektion,
  newClientProfileChecked: undefined as boolean,
  newProductAvailabilitiesSuccessStateSubject: undefined as BehaviorSubject<true>,
  submitSuccessStateSubject: undefined as BehaviorSubject<ServiceStateEnum.INIT>,
  newClientMgmCode: undefined as string,
  newClientAddress: undefined as Address,
  newClientAddressOlav: undefined as IOlavEntry,
  invoiceAddressOlav: undefined as IOlavEntry,
  newProductAvailabilities: undefined as IProduktVerfuegbarkeit[],
  newProductAvailabilitiesState: undefined as ServiceStateEnum,
  tariffSelectionState: undefined as ServiceStateEnum,
  tariffSelectionWaerme: undefined as Product,
  predefinedProductsForSelection: undefined as IProduktAuswahl,
  productsForSelection: undefined as IProduktAuswahl,
  productSelection: undefined as IProduktSelektion,
  selectedPriceIndications: undefined as PreisIndikation[],
  registrationDate: undefined as string,
  orderDetails: undefined as IOrderDetailsInfo[],
  personalData: undefined as IPersonalData,
  businessData: undefined as IBusinessData,
  organisationData: undefined as IOrganisation,
  invoiceData: undefined as IVertragskonto,
  idCard: undefined as IdCard,
  uploadedFiles: undefined as IUploadResult,
  pdfLinks: undefined as OrderIPDFLinks,
  contractDetails: undefined as IContractDetails,
  contractDetailsViewModel: undefined as NewClientContractDetailsViewModel,
  eRechnungsInfo: undefined as IERechnungsInfo,
  lastOrderDate: undefined as string,
  orderSuccessfull: undefined as boolean,
  submitSuccessState: undefined as ServiceStateEnum,
  voucherInfo: undefined as IAktionen,
  predefinedVoucherCode: undefined as string,
  preselectProductFernwaerme: undefined as boolean,
  trackingData: undefined as INewClientTrackingData,
  sendProductInterestState: undefined as ServiceStateEnum,
  hasPriceIndicationsReloadError: undefined as boolean,
  grantedMarketingPermission: undefined as boolean,
  skipPreselectedAddress: undefined as boolean,
  predefinedOlavKey: undefined as string,
  confirmationStatus: undefined as IConfirmationStatus,
  previousSelectedProductCategories: undefined as ProductCategoryEnum[],
  productsToShow: undefined as Product[],
  predefinedProducts: undefined as Product[],
  selectedProducts: undefined as Product[],
  primaryCategorySelected: undefined as Record<string, boolean>,
  campaignData: undefined as CampaignTariffSelectionData,
  newVoucherConfig: undefined as INewVoucherFormConfig,
  selectedCategories: undefined as ProductCategoryEnum[],
  initialEmail: undefined as string,
  showUpload: undefined as boolean,
  offerStatus: undefined as OfferStatusDTO,
  offerConfirmationId: undefined as string,
  offerErrorLabel: undefined as string,
  offerLoadingState: undefined as boolean,
  offerBadEmail: undefined as boolean,
};

type InitialNewClientState = typeof initialState;

export const NewClientStore = signalStore(
  { providedIn: 'root' },
  withConfirmationStatusState({
    _memoryCacheItem: MemoryCacheItem.ClearingAccountDetails,
  }),
  withStorageServiceFeature(STORAGE_KEY, true),
  withState(initialState),
  withAuthenticationStateFeature(),
  withComputed(store => {
    const stromOrGasOrderDetails = computed(() => (store.orderDetails() ?? []).filter(e => isProductCategoryStromOrGas(e.category)));
    const fernwaermeOrderDetails = computed(() => (store.orderDetails() ?? []).filter(e => isProductCategoryFernwaerme(e.category)));
    const stromOrGasProducts = computed(() =>
      sortProductsByCategory(
        stromOrGasOrderDetails()
          .map(o => getProductFromDetailsInfo(o))
          .map(p => ({
            ...p,
            tariffClasses: store.productSelection()[p.category.toLowerCase()].tarifBeschreibung,
            tarifKey: store.productSelection()[p.category.toLowerCase()].tarifKey,
          })),
        true,
      ),
    );
    const fernwaermeProducts = computed(() => fernwaermeOrderDetails().map(o => getProductFromDetailsInfo(o)));
    return {
      stromOrGasOrderDetails,
      fernwaermeOrderDetails,
      stromOrGasProducts,
      fernwaermeProducts,
    };
  }),
  withMethods(store => {
    const tariffSelectionStateService = inject(TariffSelectionStateService);
    const updateStoreState = (storeStateValues: Partial<InitialNewClientState> & Partial<ConfirmationStatusState>) => {
      if ('predefinedProducts' in storeStateValues) {
        const existing = store.predefinedProducts() ?? [];
        const merged: Product[] = sortProductsByCategory(
          [
            ...existing.filter(p => !(storeStateValues.predefinedProducts ?? []).some(n => n.category === p.category)),
            ...(storeStateValues.predefinedProducts ?? []),
          ],
          true,
        );
        const mergedCategories = new Set(merged.map(p => p.category));
        const currentProductsToShow = storeStateValues.productsToShow ?? store.productsToShow() ?? [];
        storeStateValues = {
          ...storeStateValues,
          predefinedProducts: merged,
          productsToShow: currentProductsToShow.filter(p => !mergedCategories.has(p.category)),
        };
      }
      patchState(store, { ...storeStateValues });
    };
    const hydrateOrderDetails = (store, orderDetails) => {
      const pdi = { ...orderDetails } as IOrderDetailsInfo;
      const personalData = store.personalData();
      if (!pdi.eRechnungInfo) {
        pdi.eRechnungInfo = store.eRechnungsInfo();
      }
      if (!pdi.produktDetails) {
        pdi.produktDetails = {} as IProduktDetails;
      }
      if (personalData) {
        pdi.produktDetails.partner = {
          partnerId: null,
          partnerGruppe: null,
          nickname: null,
          registrierungStatus: null,
          rolle: null,
          partnerTyp: null,
          anrede: store.personalData().salutation,
          titel: store.personalData().title?.text,
          titelKurz: store.personalData().title?.value,
          nachname: store.personalData().lastName,
          vorname: store.personalData().firstName,
          firma: null,
          email: store.personalData().email,
          telefon: store.personalData().telefonNumber,
          telefonvorwahl: store.personalData().telefonCountryCode,
        };
        pdi.produktDetails.rechnungsempfaenger = {
          partnerId: null,
          partnerGruppe: null,
          nickname: null,
          registrierungStatus: null,
          rolle: null,
          partnerTyp: null,
          titel: store.personalData().title?.value,
          anrede: store.personalData().salutation,
          nachname: store.personalData().lastName,
          vorname: store.personalData().firstName,
          firma: null,
          email: store.personalData().email,
          telefon: store.personalData().telefonNumber,
          telefonvorwahl: store.personalData().telefonCountryCode,
          adresse: {
            ort: store.newClientAddress().city,
            plz: store.newClientAddress().postcode,
            strasse: store.newClientAddress().street,
            hausnummer: store.newClientAddress().streetNumber,
            raum: null,
            land: store.newClientAddress().country,
            adresshinweis: null,
          },
        };
        pdi.partnerDetails = {
          adressen: null,
          bankkonten: null,
          geschaeftsPartnerId: null,
          kontaktdetails: [
            {
              email: { eMail: store.personalData().email },
              fax: null,
              telefon: { land: store.personalData().telefonCountryCode, nummer: store.personalData().telefonNumber },
            },
          ],
          kundensegment: null,
          nickname: null,
          organisation: null,
          systemId: null,
          physischePerson: {
            anrede: store.personalData().salutation,
            titel: store.personalData().title?.text,
            titelNachgestellt: null,
            titelVorgestellt: null,
            familienstand: null,
            geschlecht: null,
            staatsbuergerschaft: null,
            vorname: store.personalData().firstName,
            nachname: store.personalData().lastName,
            geburtsdatum: store.personalData().birthday + 'T00:00:00Z',
          },
        };
      }
      if (store.invoiceData()) {
        pdi.produktDetails.vertragskonto = store.invoiceData();
      }
      return pdi;
    };
    const getOrderData = (): IOrderData => {
      const orderDetails = store.orderDetails()?.map(od => hydrateOrderDetails(store, od));
      return {
        selectedOlav: store.newClientAddressOlav(),
        newAddress: store.newClientAddress(),
        registrationDate: store.registrationDate(),
        orderDetails: orderDetails,
        idCard: store.idCard(),
        uploadedFiles: store.uploadedFiles(),
        pdfLinks: store.pdfLinks(),
        contractDetails: store.contractDetails(),
        priceIndications: tariffSelectionStateService.indications,
        priceSheets: tariffSelectionStateService.priceSheets,
      };
    };
    return {
      updateStoreState,
      hydrateOrderDetails,
      getOrderData,
    };
  }),
);
